// src/CandlestickChart.jsx
import React, { useEffect, useRef, useState } from "react";
import { createChart, CandlestickSeries, HistogramSeries } from "lightweight-charts";

function CandlestickChart({ data, symbol, syncedTimeRange, onTimeRangeChange }) {
  const wrapperRef = useRef(null);
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const candleSeriesRef = useRef(null);
  const volumeSeriesRef = useRef(null);

  const [hover, setHover] = useState(null);
  const isSyncingRef = useRef(false);

  const formatTime = (t) => {
    if (!t) return "";
    if (typeof t === "string") return t;
    const y = t.year;
    const m = String(t.month).padStart(2, "0");
    const d = String(t.day).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  // cria chart
  useEffect(() => {
    const container = containerRef.current;
    if (!container || chartRef.current) return;

    const width =
      container.getBoundingClientRect().width > 0
        ? container.getBoundingClientRect().width
        : 600;

    const chart = createChart(container, {
      width,
      height: 340,
      layout: {
        // ✅ fundo escuro
        background: { type: "solid", color: "rgba(10,10,12,0.0)" }, // transparente p/ herdar do card
        textColor: "rgba(255,255,255,0.78)",
      },
      rightPriceScale: { borderColor: "rgba(255,255,255,0.12)" },
      timeScale: { borderColor: "rgba(255,255,255,0.12)" },
      crosshair: { mode: 1 },
      grid: {
        // ✅ grid bem sutil no dark
        vertLines: { color: "rgba(255,255,255,0.06)" },
        horzLines: { color: "rgba(255,255,255,0.06)" },
      },
    });

    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: "#26a69a",
      downColor: "#ef5350",
      borderUpColor: "#26a69a",
      borderDownColor: "#ef5350",
      wickUpColor: "#26a69a",
      wickDownColor: "#ef5350",
    });

    const volumeSeries = chart.addSeries(HistogramSeries, {
      priceFormat: { type: "volume" },
      priceScaleId: "volume",
      scaleMargins: { top: 0.8, bottom: 0 },
    });

    chart.priceScale("volume").applyOptions({
      scaleMargins: { top: 0.8, bottom: 0 },
    });

    chartRef.current = chart;
    candleSeriesRef.current = candleSeries;
    volumeSeriesRef.current = volumeSeries;

    const handleResize = () => {
      if (!chartRef.current || !containerRef.current) return;
      const newWidth = containerRef.current.getBoundingClientRect().width;
      if (newWidth > 0) chartRef.current.applyOptions({ width: newWidth });
    };

    window.addEventListener("resize", handleResize);

    // tooltip crosshair
    const handleCrosshairMove = (param) => {
      if (
        !param.time ||
        !param.point ||
        !wrapperRef.current ||
        param.point.x < 0 ||
        param.point.y < 0 ||
        param.point.x > wrapperRef.current.clientWidth ||
        param.point.y > wrapperRef.current.clientHeight
      ) {
        setHover(null);
        return;
      }

      if (!param.seriesPrices || typeof param.seriesPrices.get !== "function") {
        setHover(null);
        return;
      }

      const price = param.seriesPrices.get(candleSeries);
      if (!price) {
        setHover(null);
        return;
      }

      setHover({
        date: formatTime(param.time),
        open: price.open,
        high: price.high,
        low: price.low,
        close: price.close,
      });
    };

    chart.subscribeCrosshairMove(handleCrosshairMove);

    // sync de zoom/pan → pai
    const handleRangeChange = (range) => {
      if (!onTimeRangeChange || isSyncingRef.current) return;
      if (!range) return;
      onTimeRangeChange(range);
    };
    chart.timeScale().subscribeVisibleTimeRangeChange(handleRangeChange);

    return () => {
      window.removeEventListener("resize", handleResize);
      chart.unsubscribeCrosshairMove(handleCrosshairMove);
      chart.timeScale().unsubscribeVisibleTimeRangeChange(handleRangeChange);

      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }
      candleSeriesRef.current = null;
      volumeSeriesRef.current = null;
    };
  }, [onTimeRangeChange]);

  // aplica dados
  useEffect(() => {
    if (!candleSeriesRef.current || !volumeSeriesRef.current) return;

    if (!data || data.length === 0) {
      candleSeriesRef.current.setData([]);
      volumeSeriesRef.current.setData([]);
      return;
    }

    const candleData = data.map((p) => ({
      time: p.date,
      open: p.open != null ? p.open : p.close,
      high: p.high != null ? p.high : p.close,
      low: p.low != null ? p.low : p.close,
      close: p.close,
    }));

    const volumeData = data.map((p, idx, arr) => {
      const prev = idx > 0 ? arr[idx - 1] : p;
      const isUp = p.close >= prev.close;
      return {
        time: p.date,
        value: p.volume || 0,
        color: isUp ? "#26a69a" : "#ef5350",
      };
    });

    candleSeriesRef.current.setData(candleData);
    volumeSeriesRef.current.setData(volumeData);

    if (chartRef.current && !syncedTimeRange) {
      chartRef.current.timeScale().fitContent();
    }
  }, [data, symbol, syncedTimeRange]);

  // aplica range sincronizado vindo de fora
  useEffect(() => {
    if (!chartRef.current || !syncedTimeRange) return;
    const ts = chartRef.current.timeScale();
    isSyncingRef.current = true;
    ts.setVisibleRange(syncedTimeRange);
    isSyncingRef.current = false;
  }, [syncedTimeRange]);

  return (
    <div ref={wrapperRef} style={{ position: "relative", width: "100%", height: "100%" }}>
      <div ref={containerRef} style={{ width: "100%", height: "100%" }} />

      {hover && (
        <div
          style={{
            position: "absolute",
            top: 8,
            left: 8,
            padding: "6px 10px",
            fontSize: 11,
            // ✅ tooltip no dark
            background: "rgba(10,10,12,0.85)",
            color: "rgba(255,255,255,0.9)",
            border: "1px solid rgba(255,255,255,0.14)",
            borderRadius: 10,
            boxShadow: "0 6px 18px rgba(0,0,0,0.35)",
            pointerEvents: "none",
            backdropFilter: "blur(6px)",
          }}
        >
          <div style={{ fontWeight: 800, marginBottom: 4 }}>{hover.date}</div>
          <div style={{ opacity: 0.9 }}>O: {hover.open?.toFixed(2)}</div>
          <div style={{ opacity: 0.9 }}>H: {hover.high?.toFixed(2)}</div>
          <div style={{ opacity: 0.9 }}>L: {hover.low?.toFixed(2)}</div>
          <div style={{ opacity: 0.9 }}>C: {hover.close?.toFixed(2)}</div>
        </div>
      )}
    </div>
  );
}

export default CandlestickChart;
